import { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  FormField,
  TableSection,
  SummarySection,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import {
  ORDER_INTERNAL_ORDER_STATUS_LIST,
  ORDER_INTERNAL_PAYMENT_STATUS_LIST,
  ORDER_INTERNAL_DELIVER_STATUS_LIST,
  ORDER_INTERNAL_RECEIVING_STATUS_LIST,
  ORDER_INTERNAL_PICKUP_METHOD_LIST,
  ORDER_INTERNAL_PAYMENT_METHOD_LIST,
} from '@app/shared/utils/constants.util';
import { vietnamesePhoneNumberValidator } from '@shared/utils/phone-validator.util';
import {
  getOrderStatusLabel,
  getOrderStatusSeverity,
  getFulfillmentStatusLabel,
  getFulfillmentStatusSeverity,
} from '@shared/order-status/order-status.util';
import { Validators } from '@angular/forms';
import {
  O2O_CREATE_TAKING_DELIVERY_WAY_OPTIONS,
  O2O_TAKING_DELIVERY_WAY,
} from '@app/feature/center-business/o2o/services/o2o.config';

export const PACKAGE_FULFILLMENT_MODE = {
  virtualBundle: 'VIRTUAL_BUNDLE',
  prePacked: 'PRE_PACKED',
} as const;

export type PackageFulfillmentMode =
  (typeof PACKAGE_FULFILLMENT_MODE)[keyof typeof PACKAGE_FULFILLMENT_MODE];

export function isPrePackedPackageFulfillment(
  fulfillmentMode: unknown,
): fulfillmentMode is typeof PACKAGE_FULFILLMENT_MODE.prePacked {
  return fulfillmentMode === PACKAGE_FULFILLMENT_MODE.prePacked;
}

export function getPackageFulfillmentModeLabel(
  isPackage: boolean,
  fulfillmentMode: unknown,
): string | undefined {
  if (!isPackage) {
    return undefined;
  }

  return isPrePackedPackageFulfillment(fulfillmentMode)
    ? 'package_operation.fulfillment_mode.pre_packed'
    : 'package_operation.fulfillment_mode.virtual_bundle';
}

/**
 * Internal Order Management Configuration
 * Centralized configuration for order internal module
 */
export class InternalOrderConfig {
  // Table columns configuration
  static readonly TABLE_COLUMNS: TableColumn[] = [
    {
      label: 'order.internal.third_party_order_no',
      prop: 'outerOrderNo',
      width: 150,
    },
    {
      label: 'order.internal.internal_order_no',
      prop: 'innerOrderNo',
      width: 150,
    },
    { label: 'order.internal.store_name', prop: 'storeName', width: 120 },
    { label: 'order.internal.sales_channel', prop: 'channel', width: 120 },
    {
      label: 'order.internal.goods_quantity',
      prop: 'goodsQuatity',
      width: 100,
    },
    { label: 'order.internal.buyer_nickname', prop: 'buyerNick', width: 120 },
    { label: 'order.internal.buyer_notes', prop: 'buyerRemarks', width: 150 },
    {
      label: 'order.internal.sales_name',
      prop: 'salesEmployeeRealName',
      width: 150,
    },
    {
      label: 'order.internal.merchant_notes',
      prop: 'orderRemarks',
      width: 150,
    },
    {
      label: 'order.internal.recipient_name',
      prop: 'receiverName',
      width: 120,
    },
    { label: 'order.internal.recipient_phone', prop: 'mobile', width: 120 },
    { label: 'order.internal.recipient_address', prop: 'address', width: 200 },
    {
      label: 'order.internal.order_status',
      prop: 'orderStatus',
      width: 120,
      type: 'custom',
      template: 'statusTemplate',
      options: ORDER_INTERNAL_ORDER_STATUS_LIST,
    },
    {
      label: 'order.table.fulfillment_status',
      prop: 'fulfillmentStatus',
      width: 160,
      type: 'custom',
      template: 'statusTemplate',
    },
    {
      label: 'order.internal.receiving_status',
      prop: 'receivingStatus',
      width: 120,
      type: 'custom',
      template: 'statusTemplate',
      options: ORDER_INTERNAL_RECEIVING_STATUS_LIST,
    },
    {
      label: 'order.internal.total_amount',
      prop: 'totalAmount',
      width: 100,
      type: 'custom',
      template: 'currencyTemplate',
    },
    {
      label: 'order.internal.buyer_pay',
      prop: 'buyerPay',
      width: 100,
      type: 'custom',
      template: 'currencyTemplate',
    },
    {
      label: 'order.internal.buyer_real_price',
      prop: 'buyerRealPrice',
      width: 120,
      type: 'custom',
      template: 'currencyTemplate',
    },
    {
      label: 'order.internal.pay_status',
      prop: 'payStatus',
      width: 130,
      type: 'custom',
      template: 'statusTemplate',
      options: ORDER_INTERNAL_PAYMENT_STATUS_LIST,
    },
    {
      label: 'order.internal.order_time',
      prop: 'orderTime',
      width: 130,
      type: 'custom',
      template: 'operationTimeTemplate',
    },
    {
      label: 'order.internal.payment_time',
      prop: 'payTime',
      width: 130,
      type: 'custom',
      template: 'operationTimeTemplate',
    },
    {
      label: 'order.internal.is_gift',
      prop: 'isGift',
      width: 100,
      type: 'custom',
    },
    {
      label: 'stock_hold_timeout.order.hold_category',
      prop: 'stockHoldCategory',
      width: 160,
      type: 'custom',
      template: 'stockHoldCategoryTemplate',
    },
    {
      label: 'stock_hold_timeout.order.expiry_time',
      prop: 'stockHoldExpiryTime',
      width: 160,
      type: 'custom',
      template: 'stockHoldExpiryTemplate',
    },
    {
      label: 'stock_hold_timeout.order.failure_reason',
      prop: 'failureReasonDescription',
      width: 160,
      type: 'custom',
      template: 'failureReasonTemplate',
    },
    {
      label: 'order.internal.action',
      prop: 'actions',
      width: 130,
      type: 'actions',
    },
  ];

  // Status filter options
  static readonly STATUS_FILTER_OPTIONS = [
    { key: 'all', label: 'order.internal.status.all', active: true },
    { key: 'unfinished', label: 'order.internal.status.unfinished' },
    { key: 'completed', label: 'order.internal.status.completed' },
    { key: 'closed', label: 'order.internal.status.closed' },
    { key: 'abnormal', label: 'order.internal.status.abnormal' },
  ];

  // Primary action button — create sales order hidden
  static readonly PRIMARY_BUTTON = {
    label: 'order.internal.create_order',
    severity: 'info' as const,
    outlined: false,
    disabled: false,
    show: false,
    icon: 'add',
  };

  // Form options
  static readonly FORM_OPTIONS = {
    orderSources: ['Source 1', 'Source 2', 'Source 3'],
    salesChannels: ['online', 'offline'],
    buyerNotesOptions: ['Note 1', 'Note 2', 'Note 3'],
    giftOptions: ['Yes', 'No'],
  };

  // Pagination settings
  static readonly PAGINATION = {
    defaultPageSize: 20,
    pageSizeOptions: [10, 20, 50, 100],
  };

  // Status mapping functions
  static getOrderStatusLabel(value: number | string): string {
    // Canonical business 10-state — delegate to the SHARED util so every order view renders
    // the same label. Legacy numeric aggregate falls back to the old list.
    const shared = getOrderStatusLabel(value == null ? undefined : String(value));
    if (shared && shared.startsWith('order.status.')) {
      return shared;
    }
    const status = ORDER_INTERNAL_ORDER_STATUS_LIST.find((s) => s.value === Number(value));
    return status ? status.label : shared;
  }

  static getPaymentStatusLabel(value: number): string {
    const status = ORDER_INTERNAL_PAYMENT_STATUS_LIST.find(
      (s) => s.value === Number(value)
    );
    return status ? status.label : 'constant.status.incomplete';
  }

  static getDeliveryStatusLabel(value: number | string): string {
    const key = String(value);
    const status = ORDER_INTERNAL_DELIVER_STATUS_LIST.find((s) => s.value === key);
    return status ? status.label : 'constant.status.initial';
  }

  static getReceivingStatusLabel(value: number | string): string {
    const key = String(value);
    const status = ORDER_INTERNAL_RECEIVING_STATUS_LIST.find((s) => s.value === key);
    return status ? status.label : 'constant.status.initial';
  }

  static getPickupMethodLabel(value: any): string {
    const method = ORDER_INTERNAL_PICKUP_METHOD_LIST.find((item: any) => item.value === value);
    return method ? method.label : String(value);
  }

  static getPaymentMethodLabel(value: any): string {
    const method = ORDER_INTERNAL_PAYMENT_METHOD_LIST.find((item: any) => item.value === value);
    return method ? method.label : String(value);
  }

  // Status display methods for template
  static getStatusDisplayText(columnProp: string, value: any): string {
    switch (columnProp) {
      case 'fulfillmentStatus':
        return getFulfillmentStatusLabel(value);
      case 'orderStatus':
        return this.getOrderStatusLabel(value);
      case 'payStatus':
        return this.getPaymentStatusLabel(value);
      case 'deliveryStatus':
        return this.getDeliveryStatusLabel(value);
      case 'receivingStatus':
        return this.getReceivingStatusLabel(value);
      case 'pickupMethod':
        return this.getPickupMethodLabel(value);
      case 'payType':
        return this.getPaymentMethodLabel(value);
      default:
        return String(value);
    }
  }

  static getStatusSeverity(columnProp: string, value: any): string {
    switch (columnProp) {
      case 'fulfillmentStatus':
        return getFulfillmentStatusSeverity(value);
      case 'orderStatus':
        // Canonical business 10-state severity — delegate to the SHARED util (consistent across views).
        return getOrderStatusSeverity(String(value));
      case 'payStatus':
        return Number(value) === 1 ? 'success' : 'danger';
      case 'deliveryStatus': {
        const v = String(value);
        if (['30510', '20510', '10510'].includes(v)) return 'info';
        if (['40510', '10511', '10512'].includes(v)) return 'warn';
        if (['10513', '30512'].includes(v)) return 'success';
        if (['10514', '30513', '50510', '60510', '70510', '80510'].includes(v)) return 'danger';
        if (['30511', '30514'].includes(v)) return 'warn';
        return 'secondary';
      }
      case 'receivingStatus': {
        const v = String(value);
        if (['30520', '20520', '10520'].includes(v)) return 'info';
        if (['10521', '10522', '30522'].includes(v)) return 'success';
        if (['10523', '10524', '10525', '30521', '30523'].includes(v)) return 'danger';
        return 'secondary';
      }
      default:
        return 'secondary';
    }
  }

  static getStatusStyle(columnProp: string, value: any): any {
    const severity = this.getStatusSeverity(columnProp, value);
    const colorMap: { [key: string]: string } = {
      success: '#22c55e',
      danger: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
      secondary: '#6b7280'
    };
    
    return {
      '--status-color': colorMap[severity] || '#6b7280',
    };
  }

  static getStatusTooltip(columnProp: string, value: any): string {
    return this.getStatusDisplayText(columnProp, value);
  }
}

export interface ProductSelectionDialogConfig {
  title: string;
  width: string;
  maxWidth: string;
  height: string;
  maxHeight: string;
  searchFields: {
    key: string;
    label: string;
    placeholder: string;
  }[];
  tableColumns: TableColumn[];
}

export const PRODUCT_SELECTION_DIALOG_CONFIG: ProductSelectionDialogConfig = {
  title: 'order.internal.product_selection.title',
  width: '88vw',
  maxWidth: '90vw',
  height: 'auto',
  maxHeight: '90vh',
  searchFields: [
    {
      key: 'spuName',
      label: 'order.internal.product_selection.search.product_name',
      placeholder:
        'order.internal.product_selection.search.product_name_placeholder',
    },
    {
      key: 'spuCode',
      label: 'order.internal.product_selection.search.product_code',
      placeholder:
        'order.internal.product_selection.search.product_code_placeholder',
    },
    {
      key: 'skuCode',
      label: 'order.internal.product_selection.search.sku_code',
      placeholder:
        'order.internal.product_selection.search.sku_code_placeholder',
    },
  ],
  tableColumns: [
    {
      prop: 'spuName',
      label: 'order.internal.product_selection.table.product_name',
      width: 120,
      type: 'text',
    },
    {
      prop: 'spuCode',
      label: 'order.internal.product_selection.table.product_code',
      width: 120,
      type: 'text',
    },
    {
      prop: 'skuName',
      label: 'order.internal.product_selection.table.sku_name',
      width: 120,
      type: 'text',
    },
    {
      prop: 'skuCode',
      label: 'order.internal.product_selection.table.sku_code',
      width: 150,
      type: 'text',
    },
    {
      prop: 'brandName',
      label: 'order.internal.product_selection.table.brand',
      width: 120,
      type: 'text',
    },
    {
      prop: 'categoryName',
      label: 'order.internal.product_selection.table.category',
      width: 150,
      type: 'text',
    },
    {
      prop: 'fulfillmentModeLabel',
      label: 'package_operation.fulfillment_mode',
      width: 150,
      type: 'custom',
      template: 'fulfillmentModeTemplate',
    },
    {
      prop: 'displayPrice',
      label: 'order.internal.product_selection.table.price',
      width: 100,
      type: 'custom',
      template: 'currencyTemplate',
    },
  ],
};

// Product Service Configuration
export interface ProductServiceConfig {
  defaultPageSize: number;
  defaultSortBy: string;
  defaultAscending: boolean;
  statusFilter: number[];
  scrollHeight: string;
}

export const PRODUCT_SERVICE_CONFIG: ProductServiceConfig = {
  defaultPageSize: 20,
  defaultSortBy: 'id',
  defaultAscending: false,
  statusFilter: [3], // 3 is Effective
  scrollHeight: '470px',
};

// Order Create Form Configuration
export interface OrderCreateFormConfig {
  formFields: FormField[];
  tableSections: TableSection[];
  summarySection: SummarySection;
}

export const ORDER_CREATE_FORM_CONFIG: OrderCreateFormConfig = {
  formFields: [
    // Order Basic Information
    {
      label: 'order.internal.third_party_order_no',
      controlName: 'outerOrderNo',
      type: 'text',
      required: true,
      placeholder:
        'order.internal.form.placeholders.enter_external_order_number',
      maxLength: 64,
      section: 'o2o.create.form.sections.order_basic_information',
      colspan: 1,
      errorMessages: {
        required: 'order.internal.form.validation.order_number_required',
      },
    },
    {
      label: 'order.internal.order_date',
      controlName: 'orderTime',
      type: 'date',
      required: true,
      placeholder: 'order.internal.form.placeholders.select_order_date',
      section: 'o2o.create.form.sections.order_basic_information',
      colspan: 1,
      errorMessages: {
        required: 'order.internal.form.validation.order_date_required',
      },
    },
    {
      label: 'order.internal.sales_channel',
      controlName: 'channel',
      type: 'dropdown',
      required: false,
      placeholder: 'order.internal.form.placeholders.select_sale_channel',
      section: 'o2o.create.form.sections.order_basic_information',
      colspan: 1,
      options: [
        // { label: 'order.internal.channels.online', value: 'online' },
        { label: 'order.internal.channels.offline', value: 'offline' },
        // { label: 'order.internal.channels.o2o', value: 'o2o' },
        // { label: 'order.internal.channels.marketplace', value: 'marketplace' },
      ],
    },
    {
      label: 'order.internal.merchant_notes',
      controlName: 'orderRemarks',
      type: 'text',
      placeholder: 'order.internal.form.placeholders.enter_merchant_notes',
      maxLength: 255,
      section: 'o2o.create.form.sections.order_basic_information',
      colspan: 2,
    },

    // Fulfillment Method (aligned with O2O create)
    {
      label: 'o2o.create.form.taking_delivery_way',
      controlName: 'takingDeliveryWay',
      type: 'dropdown',
      required: true,
      placeholder: 'o2o.create.form.placeholders.select_delivery_way',
      options: O2O_CREATE_TAKING_DELIVERY_WAY_OPTIONS,
      section: 'o2o.create.form.sections.fulfillment_method',
      colspan: 1,
      defaultValue: O2O_TAKING_DELIVERY_WAY.pickup,
      errorMessages: {
        required: 'o2o.create.form.validation.taking_delivery_way_required',
      },
    },
    {
      label: 'order.internal.sales_store',
      controlName: 'storeId',
      type: 'searchableDropdown',
      required: true,
      placeholder: 'order.internal.form.placeholders.select_store',
      section: 'o2o.create.form.sections.fulfillment_method',
      colspan: 1,
      options: [], // Will be populated dynamically
      errorMessages: {
        required: 'order.internal.form.validation.store_required',
      },
    },

    // Buyer & Recipient Information
    {
      label: 'order.internal.buyer_nickname',
      controlName: 'buyerNick',
      type: 'text',
      required: true,
      placeholder: 'order.internal.form.placeholders.enter_buyer_nickname',
      section: 'o2o.create.form.sections.buyer_recipient_information',
      colspan: 1,
      errorMessages: {
        required: 'order.internal.form.validation.buyer_nickname_required',
      },
      maxLength: 128,
    },
    {
      label: 'order.internal.recipient_name',
      controlName: 'receiverName',
      type: 'text',
      required: true,
      placeholder: 'order.internal.form.placeholders.enter_recipient_name',
      section: 'o2o.create.form.sections.buyer_recipient_information',
      colspan: 1,
      errorMessages: {
        required: 'order.internal.form.validation.recipient_name_required',
      },
      maxLength: 128,
    },
    {
      label: 'order.internal.recipient_number',
      controlName: 'mobile',
      type: 'text',
      required: true,
      placeholder: 'order.internal.form.placeholders.enter_recipient_phone',
      maxLength: 20,
      section: 'o2o.create.form.sections.buyer_recipient_information',
      colspan: 1,
      validators: [vietnamesePhoneNumberValidator],
      errorMessages: {
        required: 'order.internal.form.validation.recipient_number_required',
        invalidVietnamesePhone: 'common.validation.invalid_phone_format',
      },
    },
    // {
    //   label: 'order.internal.town',
    //   controlName: 'town',
    //   type: 'text',
    //   placeholder: 'order.internal.form.placeholders.enter_town',
    //   section: 'order.internal.recipient_information',
    //   colspan: 1,
    // },
    // {
    //   label: 'order.internal.district',
    //   controlName: 'district',
    //   type: 'text',
    //   placeholder: 'order.internal.form.placeholders.enter_district',
    //   section: 'order.internal.recipient_information',
    //   colspan: 1,
    // },
    {
      label: 'order.internal.province',
      controlName: 'provinceId',
      required: true,
      type: 'searchableDropdown',
      placeholder: 'order.internal.form.placeholders.select_province',
      section: 'o2o.create.form.sections.buyer_recipient_information',
      validators: [Validators.required],
      errorMessages: {
        required: 'common.validation.required',
      },
      options: [],
    },
    {
      label: 'order.internal.address',
      controlName: 'address',
      type: 'text',
      required: true,
      placeholder: 'order.internal.form.placeholders.enter_detailed_address',
      maxLength: 255,
      section: 'o2o.create.form.sections.buyer_recipient_information',
      colspan: 1,
      errorMessages: {
        required: 'order.internal.form.validation.address_required',
      },
    },
    {
      label: 'order.internal.buyer_remarks',
      controlName: 'buyerRemarks',
      type: 'text',
      placeholder: 'order.internal.form.placeholders.enter_buyer_remarks',
      maxLength: 255,
      section: 'o2o.create.form.sections.buyer_recipient_information',
      colspan: 2,
    },

    // Payment Information
    {
      label: 'order.internal.payment_type',
      controlName: 'payType',
      type: 'dropdown',
      required: true,
      placeholder: 'order.internal.form.placeholders.select_payment_type',
      section: 'o2o.create.form.sections.order_basic_information',
      colspan: 1,
      options: [
        { label: 'common.payment_methods.cash', value: 'cash' },
        {
          label: 'common.payment_methods.wallet',
          value: 'wallet',
        },
        {
          label: 'common.payment_methods.card',
          value: 'card',
        },
      ],
      errorMessages: {
        required: 'order.internal.form.validation.payment_type_required',
      },
    },
    {
      label: 'order.internal.payment_time',
      controlName: 'payTime',
      type: 'date',
      required: true,
      placeholder: 'order.internal.form.placeholders.select_payment_time',
      section: 'o2o.create.form.sections.order_basic_information',
      colspan: 1,
      errorMessages: {
        required: 'order.internal.form.validation.payment_time_required',
      },
    },
    {
      label: 'order.internal.goods_total_amount',
      controlName: 'goodsTotalAmount',
      type: 'currency',
      required: true,
      placeholder: '0.00',
      min: 0,
      readonly: true,
      disabled: true,
      section: 'o2o.create.form.sections.payment_information',
      colspan: 1,
    },
    {
      label: 'order.internal.cost_total_amount',
      controlName: 'costTotalAmount',
      type: 'currency',
      required: true,
      placeholder: '0.00',
      min: 0,
      readonly: true,
      disabled: true,
      section: 'o2o.create.form.sections.payment_information',
      colspan: 1,
    },
    {
      label: 'order.internal.pay_amount',
      controlName: 'payAmount',
      type: 'currency',
      required: true,
      placeholder: '0.00',
      min: 0,
      readonly: true,
      disabled: true,
      section: 'o2o.create.form.sections.payment_information',
      colspan: 1,
    },
    // Shipping fee information (separate section, same radio pattern as discount)
    {
      label: 'order.internal.includes_shipping_fee',
      controlName: 'includesShippingFee',
      type: 'radio',
      required: false,
      section: 'order.internal.shipping_fee_information',
      colspan: 2,
      options: [
        { label: 'common.options.yes', value: true },
        { label: 'common.options.no', value: false },
      ],
      errorMessages: {
        required: 'order.internal.form.validation.shipping_fee_option_required',
      },
    },

    // Discount Information — only third-party platform discount radio (legacy POS UI)
    {
      label: 'order.internal.third_party_platform_discount',
      controlName: 'hasThirdPartyDiscount',
      type: 'radio',
      required: false,
      section: 'o2o.create.form.sections.discount_information',
      colspan: 2,
      options: [
        { label: 'common.options.yes', value: true },
        { label: 'common.options.no', value: false },
      ],
      errorMessages: {
        required: 'order.internal.form.validation.discount_option_required',
      },
    },

    // Tracking Number - populated by the logistics provider once the order has shipped;
    // always read-only, never user-entered. Hidden on create (no shipment exists yet);
    // the view-mode form force-unhides all delivery_information fields to display it.
    {
      label: 'o2o.create.form.tracking_number',
      controlName: 'trackingNumber',
      type: 'text',
      section: 'o2o.create.form.sections.delivery_information',
      colspan: 1,
      readonly: true,
      disabled: true,
      hidden: true,
    },
  ],
  tableSections: [
    {
      title: 'o2o.create.table.product_information',
      show: true,
      columns: [
        { prop: 'spuName', label: 'order.internal.product_name', width: 100 },
        { prop: 'spuCode', label: 'order.internal.product_code', width: 120 },
        { prop: 'skuName', label: 'order.internal.sku_name', width: 150 },
        { prop: 'skuCode', label: 'order.internal.sku_code', width: 120 },
        { prop: 'quantity', label: 'order.internal.quantity', width: 150, type: 'inputNumberHorizontal', useGrouping: true, min: 1, step: 1 },
        {
          prop: 'giftFlag',
          label: 'order.internal.gift',
          width: 60,
          type: 'custom',
        },
        // { prop: 'outerSingleSaleAmount', label: 'order.internal.platform_price', width: 100, type: 'custom', template: 'currencyTemplate' },
        // { prop: 'outerSaleAmount', label: 'order.internal.platform_total', width: 100, type: 'custom', template: 'currencyTemplate' },
        // { prop: 'innerSingleSaleAmount', label: 'order.internal.internal_price', width: 100, type: 'custom', template: 'currencyTemplate' },
        // { prop: 'innerSaleAmount', label: 'order.internal.internal_total', width: 100, type: 'custom', template: 'currencyTemplate' },
        {
          prop: 'costPrice',
          label: 'order.internal.cost_price',
          width: 100,
          type: 'custom',
          template: 'currencyTemplate',
        },
        {
          prop: 'costTotal',
          label: 'order.internal.cost_total',
          width: 100,
          type: 'custom',
          template: 'currencyTemplate',
        },
        { prop: 'shipping', label: 'order.internal.shipping', width: 80, type: 'custom', template: 'currencyTemplate', useGrouping: true },
      ],
      data: [],
      loading: false,
      hasCheckbox: true,
      selection: [],
      actions: [
        {
          label: 'order.internal.table.actions.add_product',
          action: 'addProduct',
          severity: 'info',
          outlined: true,
        },
        {
          label: 'order.internal.table.actions.delete_selected',
          action: 'deleteSelected',
          severity: 'danger',
          disabled: true,
          outlined: true,
        },
      ],
    },
  ],
  summarySection: {
    title: 'order.internal.summary.title',
    show: true,
    items: [
      {
        label: 'order.internal.summary.selected_items',
        value: 0,
        formatter: 'number',
      },
      {
        label: 'order.internal.summary.goods_total_amount',
        value: 0,
        formatter: 'currency',
        currency: 'VND',
      },
      {
        label: 'order.internal.summary.cost_total_amount',
        value: 0,
        formatter: 'currency',
        currency: 'VND',
      },
      {
        label: 'order.internal.summary.pay_amount',
        value: 0,
        formatter: 'currency',
        currency: 'VND',
      },
    ],
  },
};

/** View config aligned with Create Sales Order UI (used by standard-order-view). */
export function getStandardSalesOrderViewConfig(): {
  headerActions: ActionButton[];
  formFields: FormField[];
  tableSections: TableSection[];
  summarySection: SummarySection;
} {
  const source = ORDER_CREATE_FORM_CONFIG;
  return {
    headerActions: [],
    formFields: source.formFields.map((field) => ({ ...field })),
    tableSections: source.tableSections.map((section) => ({
      ...section,
      columns: section.columns?.map((col) => ({ ...col })) || [],
      actions: [],
      data: [],
      selection: [],
      hasCheckbox: false,
    })),
    summarySection: {
      ...source.summarySection,
      items: source.summarySection.items.map((item) => ({ ...item })),
    },
  };
}


export const ORDER_ITEM_DIALOG_CONFIG: ProductSelectionDialogConfig = {
  title: 'order.internal.order_items.dialog_title',
  width: '65vw',
  maxWidth: '66vw',
  height: 'auto',
  maxHeight: '90vh',
  searchFields: [],
  tableColumns: [
    { label: 'order.internal.order_items.product_name', prop: 'spuName', width: 150 },
    { label: 'order.internal.order_items.product_code', prop: 'spuCode', width: 120 },
    { label: 'order.internal.order_items.sku_code', prop: 'skuCode', width: 120 },
    { label: 'order.internal.order_items.quantity', prop: 'quantity', width: 80, align: 'right', type: 'custom', template: 'quantityTemplate' },
    // { label: 'order.internal.order_items.third_party_price', prop: 'thirdSaleAmount', width: 120, type: 'custom', template: 'currencyTemplate' },
    // { label: 'order.internal.order_items.third_party_total', prop: 'outBalancePrice', width: 120, type: 'custom', template: 'currencyTemplate' },
    // { label: 'order.internal.order_items.internal_price', prop: 'innerSalePrice', width: 120, type: 'custom', template: 'currencyTemplate' },
    // { label: 'order.internal.order_items.internal_total', prop: 'innerBalancePrice', width: 120, type: 'custom', template: 'currencyTemplate' },
    { label: 'order.internal.order_items.cost_price', prop: 'costAmount', width: 100, type: 'custom', template: 'currencyTemplate' },
    { label: 'order.internal.order_items.total_cost', prop: 'totalCostAmount', width: 100, type: 'custom', template: 'currencyTemplate' },
    { label: 'order.internal.order_items.total_payment', prop: 'innerSalePrice', width: 110, type: 'custom', template: 'currencyTemplate' },
    { label: 'order.internal.order_items.tags', prop: 'tags', width: 150 }
  ]
};
